function concatBytes(...chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const combined = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.length;
  }
  return combined;
}

async function hmacSha256(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const result = await crypto.subtle.sign('HMAC', cryptoKey, data);
  return new Uint8Array(result);
}

async function hkdfSha256Fallback(options: {
  secret: Uint8Array;
  salt: Uint8Array;
  info: Uint8Array;
  length: number;
}): Promise<Uint8Array> {
  const salt = options.salt.length > 0 ? options.salt : new Uint8Array(32);
  const prk = await hmacSha256(salt, options.secret);
  const blocks: Uint8Array[] = [];
  let previous = new Uint8Array(0);
  let counter = 1;
  while (blocks.reduce((sum, chunk) => sum + chunk.length, 0) < options.length) {
    const input = concatBytes(previous, options.info, new Uint8Array([counter]));
    previous = await hmacSha256(prk, input);
    blocks.push(previous);
    counter += 1;
  }
  return concatBytes(...blocks).subarray(0, options.length);
}

export async function hkdfSha256(options: {
  secret: Uint8Array;
  salt: Uint8Array;
  info: Uint8Array;
  length: number;
}): Promise<Uint8Array> {
  const { secret, salt, info, length } = options;
  try {
    const key = await crypto.subtle.importKey('raw', secret, 'HKDF', false, [
      'deriveBits',
    ]);
    const bits = await crypto.subtle.deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt,
        info,
      },
      key,
      length * 8
    );
    return new Uint8Array(bits);
  } catch (error) {
    return hkdfSha256Fallback(options);
  }
}
